// src/pages/CreateTitlePage.jsx
// Phase P3.1: Upload New Manuscript - Follows Independent Author flow
// Upload → Content Extraction → Outline Editor → TTS → Completion
// Supports Plan C mode via ?pipeline=plan_c query parameter
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import PublisherNav from '../components/PublisherNav';
import OutlineViewerModalV3 from '../components/OutlineViewerModalV3';
import StartTTSModal from '../components/StartTTSModal';
import { PlanCProgressModal, usePlanCUpload } from '../components/PlanCUploadFlow';
import { BackendUnavailableModal } from '../components/BackendUnavailableModal';
import PlanCOutlineEditorModal from '../components/PlanCOutlineEditorModal';
import IngestionModeModal from '../components/IngestionModeModal';
// P0 DIRECTIVE: TTSProgressModal is DEPRECATED and FORBIDDEN
// TTS is background-only, progress shown via polling surfaces
import { Upload, ArrowLeft, AlertTriangle, CheckSquare, Square, FileText, Loader2, Image, X, CheckCircle } from 'lucide-react';
import UniversalFooter from '../components/UniversalFooter';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { useNotification } from '@/contexts/NotificationContext';
import axios from 'axios';

const ATTESTATION_TEXT = `Publisher Upload Authorization & Rights Attestation

By proceeding, I confirm that I am acting on behalf of the Publisher identified in this account and that I am authorized to upload, distribute, and manage this work for the associated Author listed on this platform.

I attest that:

1. The Publisher has obtained all necessary rights, licenses, permissions, or contractual authority from the Author or rights holder to upload, reproduce, distribute, and make this work available through the NewAudia platform.

2. To the best of my knowledge, this work does not infringe upon any third-party copyrights, trademarks, or intellectual property rights.

3. I understand that NewAudia relies on this attestation and does not independently verify copyright ownership or authorization.

4. I acknowledge that the Publisher is solely responsible for any disputes, claims, takedown requests, or legal actions arising from the upload, distribution, or use of this work.

5. I understand that NewAudia may remove or disable access to this work if a rights dispute, legal claim, or policy violation is reported.

I confirm that this attestation is truthful and made in good faith.`;

// Import genre/theme constants
import { BOOK_TYPE_OPTIONS, GENRE_OPTIONS, getGenresForBookType, getThemesForGenre } from '../constants/genreThemes';
import { BACKEND_URL as backendUrl } from '@/config/api';
import { fetchJsonSafe } from '@/utils/fetchSafe';

const CreateTitlePage = () => {
  const { authorId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef(null);
  const coverInputRef = useRef(null);
  
  // Detect Plan C mode from query params
  const isPlanCMode = searchParams.get('pipeline') === 'plan_c';
  
  const { notifyError, notifySuccess } = useNotification();
  const [author, setAuthor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [attestationAcked, setAttestationAcked] = useState(false);
  
  // Plan C upload hook
  const planCUpload = usePlanCUpload({
    authorId,
    onSuccess: (jobData) => {
      console.log('[CreateTitlePage] Plan C job completed:', jobData);
      notifySuccess('Plan C ingestion complete!');
    },
    onError: (err) => {
      console.error('[CreateTitlePage] Plan C error:', err);
      notifyError(err.message || 'Plan C ingestion failed');
    }
  });
  
  // Form data
  const [formData, setFormData] = useState({
    title_name: '',
    subtitle: '',
    isbn: '',
    book_type: '',
    genre: '',
    theme: '',
    purchase_url: '',
    is_promotional: false,
    description: ''
  });
  
  // File states
  const [manuscriptFile, setManuscriptFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  
  // Upload progress modal
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ stage: '', percent: 0 });
  
  // Post-upload flow states (matching Independent Author Console)
  const [showOutlineModal, setShowOutlineModal] = useState(false);
  const [showStartTTSModal, setShowStartTTSModal] = useState(false);  // P0 HARD START: Explicit TTS modal
  const [showTTSStartedModal, setShowTTSStartedModal] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  
  // Created title/manuscript tracking
  const [createdTitleId, setCreatedTitleId] = useState(null);
  const [createdManuscriptId, setCreatedManuscriptId] = useState(null);
  const [pendingManuscriptData, setPendingManuscriptData] = useState(null);
  
  // Ingestion mode selection (dev/prod)
  const [showIngestionModeModal, setShowIngestionModeModal] = useState(false);
  const [selectedIngestionMode, setSelectedIngestionMode] = useState(null);
  
  const [errors, setErrors] = useState({});
  // backendUrl imported from centralized config as BACKEND_URL

  useEffect(() => {
    fetchAuthor();
  }, [authorId]);

  const fetchAuthor = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${backendUrl}/api/publisher/authors/${authorId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAuthor(data);
      } else {
        notifyError('Author not found');
        navigate('/publisher/authors');
      }
    } catch (error) {
      console.error('Error fetching author:', error);
      notifyError('Failed to load author');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['epub', 'docx'].includes(ext)) {
      notifyError('Invalid file type. Please upload EPUB or DOCX.');
      return;
    }
    
    if (file.size > 20 * 1024 * 1024) {
      notifyError('File too large. Maximum 20MB.');
      return;
    }
    
    setManuscriptFile(file);
    
    if (!formData.title_name) {
      const nameWithoutExt = file.name.replace(/\.(epub|docx)$/i, '');
      setFormData(prev => ({ ...prev, title_name: nameWithoutExt }));
    }
  };

  const handleCoverSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const allowedTypes = ['image/png', 'image/jpeg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      notifyError('Invalid file type. Please upload PNG, JPG, or WebP.');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      notifyError('File too large. Maximum 5MB.');
      return;
    }
    
    setCoverFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setCoverPreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const removeCover = () => {
    setCoverFile(null);
    setCoverPreview(null);
    if (coverInputRef.current) coverInputRef.current.value = '';
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title_name.trim()) {
      newErrors.title_name = 'Title is required';
    }
    if (!attestationAcked) {
      newErrors.attestation = 'You must acknowledge the attestation to upload';
    }
    if (!manuscriptFile) {
      newErrors.file = 'Please select a manuscript file to upload';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      notifyError('Please fix the errors before uploading');
      return;
    }
    
    // Show ingestion mode selection modal
    setShowIngestionModeModal(true);
  };

  // Called when user selects ingestion mode from modal
  const handleIngestionModeSelected = (mode) => {
    console.log(`[CreateTitlePage] Ingestion mode selected: ${mode}`);
    setSelectedIngestionMode(mode);
    
    // Store preference in localStorage
    localStorage.setItem('ingestion_mode_preference', mode);
    
    // Proceed with ingestion
    startIngestionWithMode(mode);
  };

  // Start the actual ingestion process with selected mode
  const startIngestionWithMode = async (mode) => {
    console.log(`[CreateTitlePage] Starting ingestion in ${mode} mode`);
    
    // PLAN C MODE: Use Plan C upload flow
    if (isPlanCMode) {
      console.log('[CreateTitlePage] Using Plan C upload flow');
      planCUpload.startUpload({
        title: formData.title_name,
        subtitle: formData.subtitle,
        authorName: author?.pen_name || 'Unknown Author',
        epubFile: manuscriptFile,
        coverFile: coverFile,
        ingestion_environment: mode  // Pass mode to Plan C
      });
      return;
    }
    
    // LEGACY MODE: Original upload flow
    setUploading(true);
    setShowProgressModal(true);
    setUploadProgress({ stage: 'Creating title record...', percent: 10 });
    
    try {
      const token = localStorage.getItem('accessToken');
      
      // STEP 1: Create title record
      const titlePayload = {
        title_name: formData.title_name,
        subtitle: formData.subtitle || null,
        description: formData.description || null,
        isbn: formData.isbn || null,
        book_type: formData.book_type || null,
        genre: formData.genre || null,
        theme: formData.theme || null,
        purchase_url: formData.purchase_url || null,
        is_promotional: formData.is_promotional,
        upload_authority_attestation_ack: true,
        ingestion_environment: mode,  // dev or prod
        ingestion_timestamp: new Date().toISOString()
      };
      
      // Use safe fetch helper that reads body exactly once
      const titleData = await fetchJsonSafe(`${backendUrl}/api/publisher/authors/${authorId}/titles`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(titlePayload)
      });
      
      const titleId = titleData.id;
      setCreatedTitleId(titleId);
      
      // STEP 2: Upload cover image if provided
      if (coverFile) {
        setUploadProgress({ stage: 'Uploading cover image...', percent: 20 });
        
        try {
          const coverFormData = new FormData();
          coverFormData.append('cover', coverFile);
          
          const coverResponse = await fetch(`${backendUrl}/api/publisher/titles/${titleId}/cover`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: coverFormData
          });
          
          // Consume the response body immediately to prevent clone issues
          const coverResult = await coverResponse.text();
          
          if (!coverResponse.ok) {
            console.warn('Cover upload warning:', coverResult);
          }
        } catch (coverError) {
          // Log but don't fail the entire upload for cover issues
          console.warn('Cover upload error (non-fatal):', coverError.message);
        }
      }
      
      setUploadProgress({ stage: 'Uploading manuscript...', percent: 30 });
      
      // STEP 3: Upload manuscript and trigger ingestion
      // Use XMLHttpRequest to bypass rrweb fetch interceptor
      const manuscriptFormData = new FormData();
      manuscriptFormData.append('file', manuscriptFile);
      manuscriptFormData.append('upload_authority_attestation_ack', 'true');
      manuscriptFormData.append('upload_format', manuscriptFile.name.split('.').pop().toLowerCase());
      
      const uploadData = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.withCredentials = true; // Required for CORS with credentials
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              resolve(JSON.parse(xhr.responseText));
            } catch (e) {
              reject(new Error('Invalid response from server'));
            }
          } else {
            try {
              const error = JSON.parse(xhr.responseText);
              reject(new Error(error.detail || 'Failed to upload manuscript'));
            } catch (e) {
              reject(new Error('Failed to upload manuscript'));
            }
          }
        };
        xhr.onerror = () => {
          console.error('[Upload] XHR error - possible CORS or network issue');
          reject(new Error('Network error during upload. Please try again.'));
        };
        xhr.ontimeout = () => {
          reject(new Error('Upload timed out. Please try again with a smaller file or better connection.'));
        };
        xhr.timeout = 300000; // 5 minute timeout for large files
        xhr.open('POST', `${backendUrl}/api/publisher/titles/${titleId}/upload-manuscript`);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.send(manuscriptFormData);
      });
      
      setUploadProgress({ stage: 'Processing manuscript...', percent: 60 });
      
      const manuscriptId = uploadData.manuscript_id;
      setCreatedManuscriptId(manuscriptId);
      
      // Background processing - poll until complete with exponential backoff
      if (uploadData.status === 'processing' || uploadData.next_step === 'poll_status') {
        setUploadProgress({ stage: 'Processing in background (this may take a few minutes for large files)...', percent: 65 });
        
        // Exponential backoff: 1s for first 10, then 2s, 3s, 5s, cap at 5s
        // Extended timeout: ~5 minutes total for large manuscripts
        let attempts = 0;
        const maxAttempts = 90; // ~5 minutes with backoff
        let delay = 1000;
        
        while (attempts < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, delay));
          attempts++;
          
          // Exponential backoff
          if (attempts > 10 && delay < 2000) delay = 2000;
          if (attempts > 20 && delay < 3000) delay = 3000;
          if (attempts > 30 && delay < 5000) delay = 5000;
          
          try {
            // Use safe fetch that reads body once
            const statusData = await fetchJsonSafe(`${backendUrl}/api/manuscripts/${manuscriptId}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            
            const currentStatus = statusData?.status || statusData?.processing_stage;
            
            // Update progress based on stage
            if (currentStatus === 'ingesting' || statusData?.processing_stage === 'ephemeral_intelligence') {
              setUploadProgress({ stage: 'Generating intelligence...', percent: 70 });
            } else if (statusData?.processing_stage === 'nfd_pipeline') {
              setUploadProgress({ stage: 'Extracting content structure...', percent: 80 });
            } else if (statusData?.processing_stage === 'segments_stored') {
              setUploadProgress({ stage: 'Storing segments...', percent: 85 });
            } else if (currentStatus === 'processed' || statusData?.processing_stage === 'complete' || statusData?.processing_stage === 'completed') {
              setUploadProgress({ stage: 'Content extraction complete!', percent: 90 });
              setPendingManuscriptData(statusData);
              break;
            } else if (currentStatus === 'failed') {
              throw new Error(statusData?.error_message || 'Processing failed');
            }
          } catch (pollError) {
            if (pollError.message.includes('Processing failed')) throw pollError;
            // Log status code for 520/5xx debugging
            console.warn('[Upload] Poll error:', { 
              message: pollError.message, 
              status: pollError.status,
              raw: pollError.raw?.substring(0, 200)
            });
          }
        }
        
        if (attempts >= maxAttempts) {
          // Don't throw error - just redirect to titles page
          // The background job will continue running
          notifySuccess('Processing continues in background. Redirecting to Titles page...');
          setTimeout(() => {
            navigate('/publisher/titles');
          }, 2000);
          return; // Exit gracefully, don't show error
        }
      } else {
        // Legacy immediate response
        setUploadProgress({ stage: 'Content extraction complete!', percent: 90 });
        
        // Fetch manuscript data for the outline editor
        const msResponse = await fetch(`${backendUrl}/api/manuscripts/${manuscriptId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (msResponse.ok) {
          const msData = await msResponse.json();
          setPendingManuscriptData(msData);
        }
      }
      
      setUploadProgress({ stage: 'Ready for outline review!', percent: 100 });
      
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Close progress modal and IMMEDIATELY show outline editor
      setShowProgressModal(false);
      setUploading(false);
      setShowOutlineModal(true);
      
      console.log('[SUCCESS] ' + 'Content extracted! Please review the outline.');
      
    } catch (error) {
      console.error('Error in upload flow:', error);
      notifyError(error.message || 'Upload failed');
      setShowProgressModal(false);
      setUploading(false);
    }
  };

  // Handle outline save - P0 HARD START: Show StartTTSModal, do NOT auto-trigger TTS
  const handleOutlineSave = async (data) => {
    console.log('[Publisher Upload] Outline saved, showing TTS start modal');
    setShowOutlineModal(false);
    
    // CRITICAL: Refresh manuscript data to get updated segment counts after edits
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${backendUrl}/api/v3/manuscripts/${createdManuscriptId}/outline`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const outlineData = await response.json();
        console.log('[Publisher Upload] Refreshed outline data:', outlineData);
        
        // Update pendingManuscriptData with fresh segment/word counts
        setPendingManuscriptData(prev => ({
          ...prev,
          segment_count: outlineData.segment_count,
          word_count: outlineData.total_word_count,
          character_count: outlineData.total_character_count,
          chapter_count: outlineData.segment_count
        }));
      }
    } catch (error) {
      console.error('[Publisher Upload] Failed to refresh outline data:', error);
    }
    
    // P0 HARD START: Show blocking modal - user MUST click to start TTS
    // Do NOT auto-trigger TTS here
    setShowStartTTSModal(true);
  };

  // State for TTS started confirmation modal
  const [ttsStarting, setTtsStarting] = useState(false); // Prevent double-click

  // P0 HARD START: Handle TTS started callback from StartTTSModal
  const handleTTSStarted = (result) => {
    console.log('[Publisher Upload] ✓ TTS started via Hard Start:', result);
    setShowStartTTSModal(false);
    setShowTTSStartedModal(true);
  };

  // P0 HARD START: Handle "Not Now" from StartTTSModal
  const handleTTSSkipped = () => {
    console.log('[Publisher Upload] User skipped TTS - can start later from console');
    setShowStartTTSModal(false);
    navigate(`/publisher/authors/${authorId}/console`);
  };
  
  // Handle TTS started confirmation - redirect to titles page
  const handleTTSStartedConfirm = () => {
    setShowTTSStartedModal(false);
    // Redirect to /publisher/titles with focus on the manuscript
    navigate(`/publisher/titles?focus=${createdManuscriptId}`);
  };

  // P0 FINAL HOLE: TTS polling REMOVED - Status comes from page load only
  // pollTTSProgress DELETED - No polling allowed

  // Handle completion
  const handleCompletionClose = () => {
    setShowCompletionModal(false);
    // Navigate to the Edit Title page for the newly created title
    if (createdTitleId) {
      navigate(`/publisher/titles/${createdTitleId}/edit`);
    } else {
      navigate(`/publisher/authors/${authorId}/console`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-50 overflow-x-hidden">
        <PublisherNav />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-t-indigo-500 border-r-transparent border-b-indigo-500 border-l-transparent"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50">
      <PublisherNav />
      
      <main className="mx-auto max-w-3xl px-4 py-8">
        {/* Back button */}
        <Link
          to={`/publisher/authors/${authorId}/console`}
          className="flex items-center gap-2 text-slate-400 hover:text-slate-200 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to {author?.pen_name || 'Author'}&apos;s Console
        </Link>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="rounded-full bg-indigo-600/20 p-2">
              <Upload className="w-6 h-6 text-indigo-400" />
            </div>
            <h1 className="text-2xl font-bold">Upload New Manuscript</h1>
          </div>
          <p className="text-slate-400">
            Uploading on behalf of <strong className="text-slate-200">{author?.pen_name}</strong>
          </p>
        </div>

        {/* Plan C Mode Banner */}
        {isPlanCMode && (
          <div className="mb-6 bg-cyan-900/30 border border-cyan-500/50 rounded-xl p-4" data-testid="plan-c-mode-banner">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-cyan-600/30 flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <p className="text-cyan-300 font-semibold">Processing Mode: Plan C (Durable Pipeline)</p>
                <p className="text-cyan-400/70 text-sm">Your manuscript will be processed using the Plan C durability pipeline with checkpointing and resumability.</p>
              </div>
            </div>
            
            {/* P0 DEBUG: Resume existing job button (only with ?debug=1) */}
            {new URLSearchParams(window.location.search).get('debug') === '1' && (
              <div className="mt-4 p-3 bg-yellow-900/30 border border-yellow-500/50 rounded-lg">
                <p className="text-yellow-300 text-sm font-semibold mb-2">🔧 DEBUG: Test Outline Gate</p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    id="debug-job-id"
                    placeholder="Enter job_id (e.g., pc_...)"
                    className="flex-1 bg-slate-800 border border-slate-600 rounded px-3 py-1 text-sm text-white"
                    defaultValue="pc_1768949131_4dc73fea5e5e"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const jobId = document.getElementById('debug-job-id').value;
                      if (jobId) {
                        console.log('[DEBUG] Resuming existing job:', jobId);
                        planCUpload.resumeExistingJob(jobId);
                      }
                    }}
                    className="bg-yellow-600 hover:bg-yellow-500 text-black px-4 py-1 rounded text-sm font-semibold"
                  >
                    Resume Job
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          
          {/* Book Details Section */}
          <section className="rounded-2xl border border-slate-700 bg-slate-900/50 p-6">
            <h2 className="text-lg font-semibold mb-4">Book Details</h2>
            <div className="space-y-4">
              <div>
                <Label htmlFor="title_name">Title *</Label>
                <Input
                  id="title_name"
                  name="title_name"
                  value={formData.title_name}
                  onChange={handleChange}
                  placeholder="Enter book title"
                  className={`mt-1 bg-slate-800 border-slate-600 ${errors.title_name ? 'border-red-500' : ''}`}
                />
                {errors.title_name && <p className="text-red-400 text-xs mt-1">{errors.title_name}</p>}
              </div>
              
              <div>
                <Label htmlFor="subtitle">Subtitle</Label>
                <Input id="subtitle" name="subtitle" value={formData.subtitle} onChange={handleChange}
                  placeholder="Enter subtitle (optional)" className="mt-1 bg-slate-800 border-slate-600" />
              </div>
              
              <div>
                <Label>Author</Label>
                <div className="mt-1 px-3 py-2 rounded-md bg-slate-800/50 border border-slate-700 text-slate-300">
                  {author?.pen_name}
                  <span className="text-xs text-slate-500 ml-2">(from author profile)</span>
                </div>
              </div>
              
              <div>
                <Label htmlFor="isbn">ISBN</Label>
                <Input id="isbn" name="isbn" value={formData.isbn} onChange={handleChange}
                  placeholder="Enter ISBN-10 or ISBN-13 (optional)" className="mt-1 bg-slate-800 border-slate-600" />
                <p className="text-xs text-slate-500 mt-1">Optional: ISBN for your eBook version.</p>
              </div>
              
              <div>
                <Label>Book Type</Label>
                <div className="mt-1 flex rounded-lg overflow-hidden border border-slate-600">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, book_type: 'fiction', genre: '', theme: '' }))}
                    className={`flex-1 py-2.5 px-4 text-sm font-medium transition-colors ${
                      formData.book_type === 'fiction'
                        ? 'bg-indigo-600 text-white'
                        : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                    }`}
                  >
                    Fiction
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, book_type: 'non-fiction', genre: '', theme: '' }))}
                    className={`flex-1 py-2.5 px-4 text-sm font-medium transition-colors border-l border-slate-600 ${
                      formData.book_type === 'non-fiction'
                        ? 'bg-indigo-600 text-white'
                        : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                    }`}
                  >
                    Non-Fiction
                  </button>
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="genre">Genre</Label>
                  <select id="genre" name="genre" value={formData.genre} 
                    onChange={(e) => {
                      handleChange(e);
                      // Reset theme when genre changes
                      setFormData(prev => ({ ...prev, genre: e.target.value, theme: '' }));
                    }}
                    className="mt-1 w-full rounded-md bg-slate-800 border border-slate-600 px-3 py-2 text-sm text-slate-200">
                    {getGenresForBookType(formData.book_type).map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label htmlFor="theme">Theme</Label>
                  <select id="theme" name="theme" value={formData.theme} onChange={handleChange}
                    className="mt-1 w-full rounded-md bg-slate-800 border border-slate-600 px-3 py-2 text-sm text-slate-200">
                    {getThemesForGenre(formData.genre).map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </select>
                </div>
              </div>
              
              <div>
                <Label htmlFor="purchase_url">Purchase URL</Label>
                <Input id="purchase_url" name="purchase_url" value={formData.purchase_url} onChange={handleChange}
                  placeholder="https://example.com/book (optional)" className="mt-1 bg-slate-800 border-slate-600" />
              </div>
              
              <div className="flex items-start gap-3 p-4 rounded-lg bg-slate-800/50 border border-slate-700">
                <input type="checkbox" id="is_promotional" name="is_promotional" checked={formData.is_promotional}
                  onChange={handleChange} className="mt-1 h-4 w-4 rounded border-slate-600 bg-slate-800 text-indigo-600" />
                <div>
                  <Label htmlFor="is_promotional" className="cursor-pointer font-medium">Promotional Book (Free to Read)</Label>
                  <p className="text-xs text-slate-500 mt-0.5">EUs tracked for analytics but never billed.</p>
                </div>
              </div>
            </div>
          </section>

          {/* Cover Image Section */}
          <section className="rounded-2xl border border-slate-700 bg-slate-900/50 p-6">
            <h2 className="text-lg font-semibold mb-4">Cover Image</h2>
            <input type="file" ref={coverInputRef} onChange={handleCoverSelect} accept="image/png,image/jpeg,image/webp" className="hidden" />
            
            {coverPreview ? (
              <div className="flex items-start gap-4">
                <div className="relative">
                  <img src={coverPreview} alt="Cover preview" className="w-32 h-48 object-cover rounded-lg border border-slate-600" />
                  <button type="button" onClick={removeCover} className="absolute -top-2 -right-2 p-1 rounded-full bg-red-600 hover:bg-red-500 text-white">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex-1">
                  <p className="text-sm text-slate-300 font-medium">{coverFile?.name}</p>
                  <p className="text-xs text-slate-500 mt-1">{(coverFile?.size / 1024 / 1024).toFixed(2)} MB</p>
                  <button type="button" onClick={() => coverInputRef.current?.click()} className="mt-2 text-sm text-indigo-400 hover:text-indigo-300">Change cover</button>
                </div>
              </div>
            ) : (
              <button type="button" onClick={() => coverInputRef.current?.click()}
                className="w-full p-6 rounded-xl border-2 border-dashed border-slate-600 bg-slate-800/50 hover:border-indigo-500 hover:bg-slate-800 transition-colors">
                <Image className="w-10 h-10 text-slate-500 mx-auto mb-3" />
                <p className="text-slate-300 font-medium">Click to upload cover</p>
                <p className="text-sm text-slate-500 mt-1">.jpg, .png, .webp (max 5 MB)</p>
              </button>
            )}
          </section>

          {/* Manuscript File Section */}
          <section className="rounded-2xl border border-indigo-500/30 bg-indigo-900/10 p-6">
            <h2 className="text-lg font-semibold mb-4 text-indigo-200">Manuscript File *</h2>
            <input type="file" ref={fileInputRef} onChange={handleFileSelect} accept=".epub,.docx" className="hidden" />
            
            {manuscriptFile ? (
              <div className="flex items-center gap-4 p-4 rounded-xl bg-slate-800 border border-slate-600">
                <div className="w-12 h-12 rounded-lg bg-indigo-600/20 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-indigo-400" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-slate-200">{manuscriptFile.name}</p>
                  <p className="text-sm text-slate-400">{(manuscriptFile.size / 1024 / 1024).toFixed(2)} MB</p>
                </div>
                <button type="button" onClick={() => fileInputRef.current?.click()} className="text-sm text-indigo-400 hover:text-indigo-300">Change</button>
              </div>
            ) : (
              <button type="button" onClick={() => fileInputRef.current?.click()}
                className={`w-full p-8 rounded-xl border-2 border-dashed transition-colors ${errors.file ? 'border-red-500 bg-red-900/10' : 'border-slate-600 bg-slate-800/50 hover:border-indigo-500 hover:bg-slate-800'}`}>
                <Upload className="w-10 h-10 text-slate-500 mx-auto mb-3" />
                <p className="text-slate-300 font-medium">Click to select EPUB file</p>
                <p className="text-sm text-slate-500 mt-1">.epub or .docx (max 20 MB)</p>
              </button>
            )}
            {errors.file && <p className="text-red-400 text-xs mt-2">{errors.file}</p>}
            
            {/* Manuscript Upload Policy Helper Text */}
            <p className="text-xs text-slate-500 mt-3 bg-slate-800/30 rounded-lg px-3 py-2 border border-slate-700/50" data-testid="upload-fee-notice">
              Your first manuscript is free to publish. Additional manuscripts require a $9.99 activation fee, charged when you activate the title.
            </p>
          </section>

          {/* Attestation Section */}
          <section className="rounded-2xl border border-amber-500/30 bg-amber-900/10 p-6">
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
              <div>
                <h2 className="text-lg font-semibold text-amber-200">Rights & Authority Attestation</h2>
                <p className="text-sm text-amber-300/80 mt-1">You must acknowledge this attestation to upload.</p>
              </div>
            </div>
            
            <div className="bg-slate-900/50 rounded-lg p-4 mb-4 max-h-48 overflow-y-auto text-sm text-slate-300 whitespace-pre-wrap leading-relaxed">
              {ATTESTATION_TEXT}
            </div>
            
            <button type="button" onClick={() => setAttestationAcked(!attestationAcked)}
              className={`flex items-center gap-3 w-full p-3 rounded-lg border transition-colors ${attestationAcked ? 'border-emerald-500/50 bg-emerald-900/20' : 'border-slate-600 bg-slate-800 hover:border-slate-500'} ${errors.attestation ? 'border-red-500' : ''}`}>
              {attestationAcked ? <CheckSquare className="w-5 h-5 text-emerald-400" /> : <Square className="w-5 h-5 text-slate-400" />}
              <span className={attestationAcked ? 'text-emerald-300' : 'text-slate-300'}>I acknowledge the rights attestation and confirm authority to upload.</span>
            </button>
            {errors.attestation && <p className="text-red-400 text-xs mt-2">{errors.attestation}</p>}
          </section>

          {/* Submit Buttons */}
          <div className="flex justify-end gap-4 pt-2">
            <Link to={`/publisher/authors/${authorId}/console`} className="px-6 py-2.5 rounded-full border border-slate-600 text-slate-300 hover:bg-slate-800 transition-colors">Cancel</Link>
            <button 
              type="submit" 
              disabled={uploading || planCUpload.isUploading || !attestationAcked || !manuscriptFile}
              data-testid="upload-submit-btn"
              className={`flex items-center gap-2 px-6 py-2.5 rounded-full font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                isPlanCMode 
                  ? 'bg-cyan-600 hover:bg-cyan-500 text-white' 
                  : 'bg-indigo-600 hover:bg-indigo-500 text-white'
              }`}
            >
              {uploading || planCUpload.isUploading ? (
                <><Loader2 className="w-4 h-4 animate-spin" />Uploading...</>
              ) : isPlanCMode ? (
                <><Upload className="w-4 h-4" />Upload with Plan C</>
              ) : (
                <><Upload className="w-4 h-4" />Upload & Begin</>
              )}
            </button>
          </div>
        </form>
      </main>

      {/* Ingestion Mode Selection Modal */}
      <IngestionModeModal
        isOpen={showIngestionModeModal}
        onClose={() => setShowIngestionModeModal(false)}
        onSelectMode={handleIngestionModeSelected}
      />

      {/* Plan C Progress Modal */}
      <PlanCProgressModal
        isOpen={planCUpload.showProgress}
        job={planCUpload.job}
        onClose={planCUpload.handleProgressClose}
        onResume={planCUpload.handleResume}
        onRetryWithOverwrite={planCUpload.handleRetryWithOverwrite}
        onCreateNewJob={planCUpload.handleCreateNewJob}
        onOpenOutlineEditor={planCUpload.handleOpenOutlineEditor}
        isRunning={planCUpload.isRunning}
      />

      {/* Deliverable C: Backend Unavailable Recovery Modal */}
      <BackendUnavailableModal
        isOpen={planCUpload.backendUnavailable}
        onRetry={planCUpload.handleBackendUnavailableRetry}
        onBackToDashboard={planCUpload.handleBackendUnavailableDashboard}
        error={planCUpload.lastError}
        context={{ jobId: planCUpload.job?.job_id }}
      />

      {/* Legacy Progress Modal */}
      <Dialog open={showProgressModal} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md bg-slate-900 border-slate-700 text-slate-50">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Loader2 className="w-5 h-5 text-indigo-400 animate-spin" />
              Processing Upload
            </DialogTitle>
            <DialogDescription className="text-slate-400">Please wait while we process your manuscript...</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="mb-2 flex items-center justify-between text-sm">
              <span className="text-slate-300">{uploadProgress.stage}</span>
              <span className="text-slate-400">{uploadProgress.percent}%</span>
            </div>
            <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-indigo-600 to-purple-600 transition-all duration-500" style={{ width: `${uploadProgress.percent}%` }} />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Outline Editor Modal - Shows immediately after upload (LEGACY FLOW) */}
      {showOutlineModal && createdManuscriptId && (
        <OutlineViewerModalV3
          isOpen={showOutlineModal}
          manuscriptId={createdManuscriptId}
          isStandalone={false}
          onClose={() => {
            setShowOutlineModal(false);
            navigate(`/publisher/authors/${authorId}/console`);
          }}
          onSaveAndContinue={handleOutlineSave}
        />
      )}

      {/* Plan C Outline Editor Modal - Shows when CANONICAL_OUTLINE_REQUIRED gate is reached */}
      {/* Uses Plan C's R2-based outline endpoints (GET/PUT /jobs/{job_id}/outline), NOT legacy MongoDB */}
      {/* P0 DEBUG: Log render condition */}
      {(() => {
        const shouldRender = isPlanCMode && planCUpload.job?._showOutlineEditor && !!planCUpload.job?.job_id;
        console.log('[CreateTitlePage] PlanCOutlineEditorModal RENDER_CHECK', {
          isPlanCMode,
          _showOutlineEditor: planCUpload.job?._showOutlineEditor,
          job_id: planCUpload.job?.job_id,
          shouldRender
        });
        return null;
      })()}
      <PlanCOutlineEditorModal
        isOpen={isPlanCMode && planCUpload.job?._showOutlineEditor && !!planCUpload.job?.job_id}
        jobId={planCUpload.job?.job_id}
        onClose={planCUpload.handleOutlineClose}
        onSaveAndContinue={planCUpload.handleOutlineSave}
      />

      {/* P0 HARD START: StartTTSModal - User must explicitly click to start TTS */}
      <StartTTSModal
        isOpen={showStartTTSModal}
        onClose={handleTTSSkipped}
        titleId={createdTitleId}
        manuscriptData={pendingManuscriptData}
        onTTSStarted={handleTTSStarted}
        forceReset={true}
      />

      {/* TTS Started Confirmation Modal - NEW: Replaces TTSProgressModal as default */}
      <Dialog open={showTTSStartedModal} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md bg-slate-900 border-slate-700 text-slate-50">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-6 h-6 text-emerald-400" />
              Audio Generation Started
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-300 mb-4">
              Audio generation is running in the background. You will now be redirected to <strong>Titles</strong> where progress is tracked.
            </p>
            <p className="text-slate-400 text-sm">
              This process typically takes 30-60 minutes to complete. You can safely close your browser and return later.
            </p>
          </div>
          <div className="flex justify-center">
            <button 
              onClick={handleTTSStartedConfirm}
              className="px-6 py-2 rounded-full bg-indigo-600 hover:bg-indigo-500 text-white font-semibold"
            >
              OK
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* P0 DIRECTIVE: TTSProgressModal is FORBIDDEN and has been REMOVED */}
      {/* TTS progress is shown via polling on /publisher/titles page */}

      {/* Completion Modal */}
      <Dialog open={showCompletionModal} onOpenChange={handleCompletionClose}>
        <DialogContent className="sm:max-w-md bg-slate-900 border-slate-700 text-slate-50">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-6 h-6 text-emerald-400" />
              Upload Complete!
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 text-center">
            <p className="text-slate-300 mb-4">
              Your manuscript has been processed and audio has been generated successfully.
            </p>
            <p className="text-slate-400 text-sm">
              The title is now ready to be activated for reader interactions.
            </p>
          </div>
          <div className="flex justify-center">
            <button onClick={handleCompletionClose}
              className="px-6 py-2 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white font-semibold">
              Go to Console
            </button>
          </div>
        </DialogContent>
      </Dialog>
      <UniversalFooter />
    </div>
  );
};

export default CreateTitlePage;
